import React, { useEffect, useState } from 'react';
import { SearchList } from '../../../../components/lists/search-list';
import { ListItem } from '../../../../components/lists/list-item';
import { useAppActions, useAppState } from '../../../../overmind';
import { ActivationStatus, SortField } from '../../../../enums';
import { IPeopleComparisonListProps, Person } from './people-comparison.models';
import { PagingSearchParams, SearchingSearchParams } from '../../../../models/overmind/search-params';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { useMatchScreenWidth } from '@keplerco/core';
import { PeopleSearchRequest } from '../../../../models/overmind/search-request';
import { CohortType } from '../../../../components/panels/select-cohort/select-cohort.models';

export function PeopleComparisonList({ selectedCohortType, selectedEntity, pendingPeopleSlugs, setPendingPeopleSlugs }: IPeopleComparisonListProps): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const isMobile = useMatchScreenWidth('mobile');

  const [loading, setLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<SearchingSearchParams & PagingSearchParams>({
    search: undefined,
    page: 1,
    pageSize: 5,
  });
  const [people, setPeople] = useState<Person[]>();
  const [totalPages, setTotalPages] = useState<number>(0);

  useEffect(() => {
    async function getData() {
      setLoading(true);

      const request: PeopleSearchRequest = {
        companySlug: companyVariables.slug!,
        search: searchParams.search,
        page: searchParams.page,
        pageSize: searchParams.pageSize,
        sortField: SortField.Name,
        activationStatuses: [ActivationStatus.Active],
        sortAscending: true,
      };
      if (selectedCohortType === CohortType.Department && !!selectedEntity) request.departmentSlug = selectedEntity.slug;
      if (selectedCohortType === CohortType.Team && !!selectedEntity) request.teamSlug = selectedEntity.slug;

      const response = await actions.searchPeople(request);
      setPeople(
        response?.employees.map(employee => ({
          name: `${employee.firstName} ${employee.lastName}`,
          slug: employee.learnerSlug ?? employee.id,
          department: employee.department?.name ?? 'No department',
          email: employee.email,
        }))
      );
      setTotalPages(response?.totalPages ?? 0);

      setLoading(false);
    }

    getData();
  }, [searchParams, selectedCohortType, selectedEntity, companyVariables.slug]);

  return (
    <div className="card">
      <SearchList
        loading={loading}
        onInput={search => setSearchParams(currentState => ({ ...currentState, search }))}
        paging={{
          activePageNumber: searchParams.page ?? 1,
          pageCount: totalPages,
          onPageChange: page => setSearchParams(currentState => ({ ...currentState, page })),
        }}
        emptyState
      >
        {people?.map(person => {
          const selected = pendingPeopleSlugs.includes(person.slug);
          return (
            <ListItem
              key={person.slug}
              selected={selected}
              onClick={() => {
                if (!selected && pendingPeopleSlugs.length === 5) {
                  actions.addNotification({
                    active: true,
                    id: crypto.randomUUID(),
                    type: NotificationType.Error,
                    priority: NotificationPriority.Toast,
                    toastIncludeIcon: true,
                    title: 'Only 5 people can be selected',
                    message: 'Please deselect a selected person in order to select this person',
                  });
                  return;
                }

                setPendingPeopleSlugs(selected ? pendingPeopleSlugs.filter(slug => slug !== person.slug) : [...pendingPeopleSlugs, person.slug]);
              }}
            >
              <div style={{ display: 'grid', gap: 15, alignItems: 'center', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr' }}>
                <span style={{ color: 'var(--accent-2)' }}>{person.name}</span>
                <small>{person.department}</small>
                <small>{person.email}</small>
              </div>
            </ListItem>
          );
        })}
      </SearchList>
    </div>
  );
}
